import { Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import { FC, createRef } from "react";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  OptionsDataType,
  RowProps,
} from "../../../../../components";
import MuiChipWithPopover from "../../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import { StatusBadge } from "../../../../../components/statusBadge/StatusBadge";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
} from "../../../../../helpers/constants";
import {
  TableActionType,
  TableActions,
} from "../../../../../helpers/types/common";
import { buildActionMenuItems, isNotEmpty } from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import {
  CreateClientActivitiesFormKeys,
  CreateClientActivitiesTabProps,
} from "../modelsAndSchemas/models";

export const pageId = "Create-Client-Activities-tab";

export const CreateClientActivitiesTab: FC<CreateClientActivitiesTabProps> = ({
  rows,
  control,
  tableRef,
  isView,
  uniqueFilters,
  filteredResults,
  onActivityViewClick,
  deleteAction,
}) => {
  const { fetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const onActionClick = (action: TableActionType, selectedRow: any) => {
    if (action === TableActions.edit) {
      onActivityViewClick(selectedRow, true);
    } else {
      deleteAction(selectedRow);
    }
  };
  const renderFormControl = (
    control: any,
    name: keyof typeof CreateClientActivitiesFormKeys,
    options: OptionsDataType[],
    placeholder: string,
    id: string,
  ) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={options}
        id={id}
        placeholder={placeholder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const tableHeaderForm = [
    renderFormControl(
      control,
      CreateClientActivitiesFormKeys.activityType,
      uniqueFilters.activityType,
      commonLabels.selectPlaceHolder,
      `${pageId}-${CreateClientActivitiesFormKeys.activityType}`,
    ),
    renderFormControl(
      control,
      CreateClientActivitiesFormKeys.modality,
      uniqueFilters.modality,
      commonLabels.selectPlaceHolder,
      `${pageId}-${CreateClientActivitiesFormKeys.modality}`,
    ),
    <FormController
      control={control}
      name={CreateClientActivitiesFormKeys.topic}
      key={CreateClientActivitiesFormKeys.topic}
    >
      <MuiTextField
        id={`${pageId}-${CreateClientActivitiesFormKeys.topic}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFormControl(
      control,
      CreateClientActivitiesFormKeys.candidateStatuses,
      fetchDropdownValues(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.listParticipantStatusList,
        true,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${CreateClientActivitiesFormKeys.candidateStatuses}`,
    ),
    renderFormControl(
      control,
      CreateClientActivitiesFormKeys.activityStatus,
      fetchDropdownValues(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.listStatusList,
        true,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${CreateClientActivitiesFormKeys.activityStatus}`,
    ),
    renderFormControl(
      control,
      CreateClientActivitiesFormKeys.units,
      uniqueFilters.units,
      commonLabels.selectPlaceHolder,
      `${pageId}-${CreateClientActivitiesFormKeys.units}`,
    ),
    <></>,

    <></>,
  ];

  const columns = [
    {
      id: CreateClientActivitiesFormKeys.activityType,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.activityTypeHeader,
      ),
      render: (row: any) =>
        !row.activityType ? null : (
          <MuiLink onClick={() => onActivityViewClick(row)}>
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link"
                text={row.activityType}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
      isSortVisible: true,
      className: "cell-activity-type",
    },
    {
      id: CreateClientActivitiesFormKeys.modality,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.modalityHeader,
      ),
      isSortVisible: true,
      className: "cell-modality",
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={row.modality?.map((item: string) => ({
            label: item,
            value: item,
          }))}
          id={"selected-modality"}
          isEditable={false}
          headerLabel={fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.activitiesList,
            activitiesAndDeliverablesSiteCoreKeys.modalityHeader,
          )}
          isDialogTitleWithEllipsis
          gridRef={createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: CreateClientActivitiesFormKeys.topic,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.topicHeader,
      ),
      isSortVisible: true,
      className: "cell-topics",
      render: (row: any) => (
        <MuiBox>
          <MuiEllipseLabel
            labelTypoClassName="Link ellipsis-with-tooltip"
            text={row.topicName}
            labelTypoVariant="h3"
            placement="bottom-start"
          />
        </MuiBox>
      ),
    },
    {
      id: CreateClientActivitiesFormKeys.candidateStatuses,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.participantStatusHeader,
      ),
      isSortVisible: true,
      className: "cell-candidate-status",
      render: (row: RowProps) =>
        isNotEmpty(row.candidateStatuses) ? (
          <MuiChipWithPopover
            data={row.candidateStatuses?.map((item: string) => ({
              label: item,
              value: item,
            }))}
            id={"selected-candidate-statuses"}
            isEditable={false}
            headerLabel={fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.activitiesList,
              activitiesAndDeliverablesSiteCoreKeys.participantStatusHeader,
            )}
            isDialogTitleWithEllipsis
            countToWrap={1}
            showMoreARIALabel={commonARIALabel.showMoreDetails}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
            gridRef={createRef<HTMLElement>()}
          />
        ) : null,
    },

    {
      id: CreateClientActivitiesFormKeys.activityStatus,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.activityStatusHeader,
      ),
      isSortVisible: true,
      className: "cell-activity-status",
      render: (row: any) => (
        <StatusBadge
          status={row.activityStatusKey}
          label={row.activityStatus}
        />
      ),
    },

    {
      id: CreateClientActivitiesFormKeys.units,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.unitsHeader,
      ),
      isSortVisible: true,
      className: "cell-units",
    },
    {
      id: CreateClientActivitiesFormKeys.measurement,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.measurementHeader,
      ),
      isSortVisible: true,
      className: "cell-measurement",
    },

    {
      id: "action",
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.actionHeader,
      ),
      render: (selectedRow: any) => {
        const clientActivitiesActions = [
          {
            permissionKey: MODULE_PERMISSIONS.EDIT_CLIENT_SPECIFIC_ACTIVITIES,
            create: () => ({
              onClick: () => onActionClick(TableActions.edit, selectedRow),
              label: commonLabels.edit,
              dataTestId: "editActivity",
              ariaLabel: commonLabels.edit,
            }),
          },
          {
            permissionKey: MODULE_PERMISSIONS.DELETE_CLIENT_SPECIFIC_ACTIVITIES,
            create: () => ({
              onClick: () => onActionClick(TableActions.delete, selectedRow),
              label: commonLabels.delete,
              dataTestId: "deleteActivity",
              ariaLabel: commonLabels.delete,
            }),
          },
        ];

        const clientActivitiesMenuItems = buildActionMenuItems(
          selectedRow,
          clientActivitiesActions,
          hasModulePermission,
        );

        return clientActivitiesMenuItems ? (
          <MuiOptionMenu menuItems={clientActivitiesMenuItems} />
        ) : null;
      },
    },
  ];

  return (
    <MuiGrid container data-testid="search-activities">
      <MuiGrid size={{ xs: 12 }} className="header-right">
        <MuiTable
          ref={tableRef}
          pageId={pageId}
          tableHeaderForm={
            isView ? tableHeaderForm.slice(0, -1) : tableHeaderForm
          }
          columns={isView ? columns.slice(0, -1) : columns}
          tableResults={[...filteredResults]}
          noDataInApi={rows.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.activitiesList,
            activitiesAndDeliverablesSiteCoreKeys.activitiesNotAvailable,
          )}
          priorityResults={[]}
          isLastColumnSticky={!isView}
        />
      </MuiGrid>
    </MuiGrid>
  );
};
